import React, { useEffect, useState } from 'react';
import { useHistory, Link, Prompt } from 'react-router-dom';
import { Button, Flex, message, Typography, Form, Input, Upload as AntUpload, Space, Tag, Segmented, Switch } from 'antd';
import { Card, CardBody, Container } from 'reactstrap';
import { Col, Row } from 'react-bootstrap';
import Palette from '../../../utils/Palette';
import Iconify from '../../reusable/Iconify';
import swal from '../../reusable/CustomSweetAlert';
import EBulletin from 'models/EBulletinModel';
import Upload from 'models/UploadModel';

export default function EBulletinFormPage({
                                            eBulletinData,
                                            disabled,
                                          }) {
  const history = useHistory();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [form] = Form.useForm();
  const [formDisabled, setFormDisabled] = useState(false);
  const [language, setLanguage] = useState("ID");
  const [hasChanges, setHasChanges] = useState(false);

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreviewURL, setCoverPreviewURL] = useState(null);

  const allowedImageType = ["image/jpg", "image/jpeg", "image/png", "image/webp"]

  const getFileName = (url) => {
    try {
      return decodeURIComponent(String(url).split("/").pop().split("?")[0]);
    } catch {
      return String(url);
    }
  }

  const uploadPdf = async () => {
    try {
      const result = await Upload.uploadFile(pdfFile);
      form.setFieldValue("url", result?.location);
      setPdfUrl(result?.location);
      setPdfFile(null);
      message.success("PDF uploaded successfully");
    } catch (e) {
      console.log("upload pdf error", e);
      message.error("Failed to upload PDF");
      throw e;
    }
  }

  const onUploadChange = ({ file }) => {
    const isPdf = file.type === "application/pdf";
    if (!isPdf) {
      message.error("File must be PDF (.pdf)");
      return Upload.LIST_IGNORE;
    }
    const lessThan50MB = file.size / 1024 / 1024 < 50;
    if (!lessThan50MB) {
      message.error("PDF must be smaller than 50MB.");
      return Upload.LIST_IGNORE;
    }

    setPdfFile(file);
  }

  const uploadCover = async () => {
    try {
      const result = await Upload.uploadPicutre(coverFile);
      form.setFieldValue("cover_image", result?.location);
      setCoverPreviewURL(result?.location);
      message.success("Cover uploaded successfully");
    } catch (e) {
      console.log("upload cover error", e);
      message.error("Failed to upload cover");
      throw e;
    }
  }

  const onCoverChange = ({ file }) => {
    const isImage = allowedImageType.includes(file.type);
    if (!isImage) {
      message.error("File must be image type " +
        allowedImageType.map((type) => type.slice(6).toUpperCase()).join(", "))
      return Upload.LIST_IGNORE;
    }
    const lessThan5MB = file.size / 1024 / 1024 < 5;
    if (!lessThan5MB) {
      message.error("Image must be smaller than 5MB.")
      return Upload.LIST_IGNORE;
    }

    setCoverFile(file);
    const url = URL.createObjectURL(file);
    setCoverPreviewURL(url);
  }

  const onValuesChanged = (changedValues, allValues) => {
    if (!eBulletinData) {
      const changed = Object.keys(allValues).some((key) => {
        if (allValues[key]) {
          return true
        }
        return false
      })
      return setHasChanges(changed || !!pdfFile || !!coverFile)
    }
    const changed = Object.keys(allValues).some((key) => {
      if (!!allValues[key] && allValues[key] != eBulletinData[key]) {
        return true
      }
      return false
    })
    setHasChanges(changed || !!pdfFile || !!coverFile);
  }

  const onSubmit = async () => {
    setLoadingSubmit(true)
    try {
      if (pdfFile) {
        await uploadPdf();
      }
      if (coverFile) {
        await uploadCover();
      }
      const body = form.getFieldsValue()
      if (!body.url) {
        await swal.fire({
          title: 'PDF belum ada',
          text: 'Upload file PDF dulu sebelum menyimpan.',
          icon: 'warning',
          confirmButtonText: 'Okay'
        })
        return
      }

      let msg
      if (!eBulletinData?.id) {
        msg = 'Successfully added new E-Bulletin'
        await EBulletin.create(body);
      } else {
        msg = 'Successfully updated E-Bulletin'
        await EBulletin.edit(eBulletinData.id, body);
      }

      message.success(msg)
      history.push("/e-bulletins")
    } catch (e) {
      console.log(e)
      const errorMessage = "An Error Occured"
      await swal.fire({
        title: 'Error',
        text: e.error_message ? e.error_message : errorMessage,
        icon: 'error',
        confirmButtonText: 'Okay'
      })
    } finally {
      setLoadingSubmit(false)
    }
  }

  const languageTag = (text, tagColor = Palette.MAIN_THEME) => (
    <span>
      {text} | {language}{" "}
      <Tag color={tagColor} style={{ fontSize: '10px', marginLeft: '8px' }}>
        Multi-Language
      </Tag>
    </span>
  );

  useEffect(() => {
    if (eBulletinData?.id) {
      form.setFieldsValue({
        name: eBulletinData.name,
        name_tl: eBulletinData.name_tl,
        url: eBulletinData.url,
        cover_image: eBulletinData.cover_image,
        is_spread: eBulletinData.is_spread,
      })
      if (eBulletinData.url) {
        setPdfUrl(eBulletinData.url);
      }
      if (eBulletinData.cover_image) {
        setCoverPreviewURL(eBulletinData.cover_image);
      }
    }
    if (disabled) {
      setFormDisabled(disabled);
    }
  }, [eBulletinData])

  return (
    <>
      <Container fluid>
        <Card style={{ background: Palette.BACKGROUND_DARK_GRAY, color: "white" }}
              className="card-stats mb-4 mb-xl-0">
          <CardBody>
            <Row>
              <Col className='mb-3' md={6}>
                <Space align='center'>
                  <Link to={'/e-bulletins'}>
                    <Space align='center'>
                      <Iconify icon={'material-symbols:arrow-back-rounded'} style={{ fontSize: "16px", color: "white" }}></Iconify>
                    </Space>
                  </Link>
                  <span style={{ fontWeight: "bold", fontSize: "1.1em" }}>E-Bulletin</span>
                </Space>
              </Col>
            </Row>
            <Row>
              <Col className='mb-3' md={12} style={{ marginTop: "40px" }}>
                <Typography.Title level={3}>{!eBulletinData?.id ? "Add" : "Update"} E-Bulletin</Typography.Title>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form
                  layout='vertical'
                  form={form}
                  initialValues={{ is_spread: true }}
                  onFinish={onSubmit}
                  onValuesChange={onValuesChanged}
                  validateTrigger="onSubmit"
                  disabled={formDisabled}
                  autoComplete='off'
                >
                  <Flex gap={"48px"} >
                    <Flex vertical style={{ width: "100%" }}>
                      <Flex justify='space-between' align='center'>
                        <Flex align='center' gap={12}>
                          <Typography.Text>
                            Spread layout (PDF halaman bentang)
                          </Typography.Text>
                          <Form.Item
                            label={"Spread layout"}
                            name={"is_spread"}
                            valuePropName='checked'
                            noStyle
                          >
                            <Switch />
                          </Form.Item>
                        </Flex>

                        <Segmented
                          value={language}
                          style={{ marginBottom: 8 }}
                          onChange={setLanguage}
                          options={['ID', 'EN']}
                        />
                      </Flex>
                      <Form.Item
                        label={languageTag("Name")}
                        name={"name"}
                        rules={[{
                          required: true,
                        }]}
                        hidden={language !== "ID"}
                      >
                        <Input variant='filled' placeholder="e.g. Edisi 1" />
                      </Form.Item>
                      <Form.Item
                        label={languageTag("Name")}
                        name={"name_tl"}
                        hidden={language === "ID"}
                      >
                        <Input variant='filled' placeholder="e.g. Edition 1 (Translated Version)" />
                      </Form.Item>

                      <Form.Item
                        label={"PDF File"}
                        name={"url"}
                        hidden
                      >
                        <Input />
                      </Form.Item>
                      <div style={{ marginBottom: 24, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Typography.Text>
                          PDF File <span style={{ color: "#ff4d4f" }}>*</span>
                        </Typography.Text>
                        <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <AntUpload
                            accept="application/pdf"
                            maxCount={1}
                            beforeUpload={() => false}
                            onChange={onUploadChange}
                            showUploadList={false}
                          >
                            <Button
                              size="sm"
                              type="default"
                              icon={<Iconify icon={"material-symbols:picture-as-pdf-outline"} style={{ fontSize: "16px" }} />}
                            >
                              {pdfFile || pdfUrl ? "Replace PDF" : "Upload PDF"}
                            </Button>
                          </AntUpload>
                          {pdfUrl && (
                            <Button
                              size="sm"
                              type="default"
                              onClick={() => window.open(pdfUrl, "_blank")}
                            >
                              Lihat PDF
                            </Button>
                          )}
                        </div>
                        {pdfFile && (
                          <Typography.Text type="secondary" style={{ display: "block", marginTop: 8, wordBreak: "break-all" }}>
                            {pdfFile.name} (belum disimpan — klik Save)
                          </Typography.Text>
                        )}
                        {!pdfFile && pdfUrl && (
                          <Typography.Text type="secondary" style={{ display: "block", marginTop: 8, wordBreak: "break-all" }}>
                            {getFileName(pdfUrl)}
                          </Typography.Text>
                        )}
                      </div>

                      <Typography.Text>
                        Untuk performa lebih baik, jangan gunakan mode bentang dan harap pdf di format satu halaman per satu halaman pdf seperti <a href={"https://readindonesia.id/pdf/ReadIndonesia%20Desember%202025_02.pdf"} target={"_blank"}>contoh</a>
                      </Typography.Text>

                      <Form.Item
                        label={"Cover Image"}
                        name={"cover_image"}
                        hidden
                      >
                        <Input />
                      </Form.Item>
                      <div style={{ marginBottom: 24, marginTop  :24 }}>
                        <Typography.Text>Cover Image</Typography.Text>
                        <div style={{ marginTop: 8 }}>
                          {coverPreviewURL && (
                            <div style={{ marginTop: 12, maxWidth: 200, marginBottom : 12 }}>
                              <img
                                src={coverPreviewURL}
                                alt="cover preview"
                                style={{ width: "100%", objectFit: "contain", borderRadius: 8 }}
                              />
                            </div>
                          )}
                          <AntUpload
                            accept="image/jpg,image/jpeg,image/png,image/webp"
                            maxCount={1}
                            beforeUpload={() => false}
                            onChange={onCoverChange}
                            showUploadList={false}
                          >
                            <Button size="sm" type="default">
                              {coverPreviewURL ? "Replace Cover" : "Upload Cover"}
                            </Button>
                          </AntUpload>
                        </div>
                      </div>

                      {!formDisabled ? (
                        <div className={"d-flex flex-row"} style={{ gap: "12px" }}>
                          <Button size="sm" type='primary' variant="primary" htmlType='submit' loading={loadingSubmit}>
                            {!eBulletinData?.id ? "Add E-Bulletin" : "Save E-Bulletin"}
                          </Button>
                        </div>
                      ) : (
                        <></>
                      )}
                    </Flex>
                    <Flex vertical style={{ width: "30%" }} className='text-white'>
                    </Flex>
                  </Flex>
                </Form>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
      <Prompt
        when={hasChanges && !loadingSubmit}
        message={"Are you sure you want to leave before saving?"}
      />
    </>
  );
}
